import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import Logo from "../AllImages/GetItRight.png";
import MajesticConcert from "../Assets/oSa.jpg";

// Japanese theme assets
import Cherry from "../Assets/Cherry.png";
import BrushOne from "../Assets/BrushOne.png";
import BrushTwo from "../Assets/BrushTwo.png";
import BrushThree from "../Assets/BrushThree.png";
import BrushFour from "../Assets/BrushFour.png";

// Firebase imports
import { db } from "../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";

// Default payments for May–November
const initialMonthsState = {
  May: false,
  June: false,
  July: false,
  August: false,
  September: false,
  October: false,
  November: false,
};

// Participant template including password
const getNewParticipant = () => ({
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  phone: "",
  age: "",
  role: "",
  instrument: "",
  nationality: "",
  address: "",
  dietary: "",
  singleBed: false,
  payments: { ...initialMonthsState },
  initialPayment: false,
});

// Country options for Nationality dropdown
const countryOptions = [
  "United States", "Canada", "Mexico", "United Kingdom", "Germany", "France", "Italy", "Spain",
  "Japan", "China", "South Korea", "Australia", "Brazil", "Argentina", "Chile", "Colombia",
  "Peru", "Russia", "India", "South Africa", "New Zealand", "Netherlands", "Sweden", "Norway",
  "Denmark", "Finland", "Switzerland", "Austria", "Belgium", "Portugal", "Ireland", "Poland",
  "Czech Republic", "Slovakia", "Hungary", "Romania", "Bulgaria", "Greece", "Turkey", "Egypt",
  "Morocco", "Nigeria", "Kenya", "Thailand", "Vietnam", "Singapore", "Malaysia", "Indonesia",
  "Philippines"
];

// Orchestral instrument options
const instrumentOptions = [
  "Violin", "Viola", "Cello", "Double Bass", "Harp", "Piano",
  "Flute", "Piccolo", "Oboe", "English Horn", "Clarinet", "Bass Clarinet", "Bassoon", "Contrabassoon",
  "French Horn", "Trumpet", "Trombone", "Bass Trombone", "Tuba",
  "Timpani", "Percussion"
];

// Decorative brush strokes in background
function BrushBackgrounds() {
  const brushes = [
    { src: BrushOne, style: { top: '5%', left: '2%', width: '160px', rotate: '-12deg' } },
    { src: BrushTwo, style: { top: '35%', right: '4%', width: '180px', rotate: '10deg' } },
    { src: BrushThree, style: { top: '70%', left: '10%', width: '150px', rotate: '-8deg' } },
    { src: BrushFour, style: { bottom: '10%', right: '5%', width: '160px', rotate: '12deg' } },
  ];

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden hidden sm:block">
      {brushes.map((brush, i) => (
        <img
          key={i}
          src={brush.src}
          alt={`Brush ${i}`}
          className="absolute"
          style={{
            ...brush.style,
            transform: `rotate(${brush.style.rotate})`,
          }}
        />
      ))}
    </div>
  );
}

// Falling cherry petals animation
function FallingPetals({ count = 20 }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
      {Array.from({ length: count }).map((_, i) => {
        const delay = Math.random() * 20;
        const duration = 20 + Math.random() * 20;
        const startX = Math.random() * 100;
        const drift = (Math.random() - 0.5) * 20;

        return (
          <motion.img
            key={i}
            src={Cherry}
            className="absolute w-5 h-5"
            style={{ left: `${startX}%` }}
            initial={{ y: '-15vh', opacity: 1, rotate: Math.random() * 360 }}
            animate={{ y: '115vh', x: `${startX + drift}%`, opacity: 0, rotate: Math.random() * 720 }}
            transition={{ repeat: Infinity, duration, delay, ease: 'linear' }}
          />
        );
      })}
    </div>
  );
}

export default function OnboardPage() {
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(getNewParticipant());

  const handleChange = (field, value) => {
    setParticipant(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const p = participant;
    const required = ["firstName","lastName","email","password","phone","age","role","nationality","address"];
    for (let f of required) {
      if (!p[f].toString().trim()) {
        alert(`Por favor completa el campo: ${f}`);
        return false;
      }
    }
    if (p.password.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres.");
      return false;
    }
    if (p.role === "musician" && !p.instrument.trim()) {
      alert("Selecciona un instrumento.");
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    const auth = getAuth();
    let user;
    try {
      const cred = await createUserWithEmailAndPassword(auth, participant.email, participant.password);
      user = cred.user;
    } catch (err) {
      console.error(err);
      alert("Error al crear usuario. Intenta nuevamente.");
      return;
    }
    const data = { participant, createdAt: serverTimestamp(), accountType: "Client" };
    try {
      await setDoc(doc(db, "reservations", user.uid), data);
    } catch (err) {
      console.error(err);
      alert("Error al guardar reserva.");
      return;
    }
    navigate("/DashBoard", { state: { uid: user.uid, name: `${participant.firstName} ${participant.lastName}` } });
  };

  return (
    <div className="relative font-sans min-h-screen bg-white text-black">
      <BrushBackgrounds />
      <FallingPetals />

      {/* Header */}
      <div className="relative h-64 flex items-center justify-center">
        <img src={MajesticConcert} alt="Background" className="absolute w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-4">
          <img src={Logo} alt="Logo" className="h-12 mb-2 mx-auto" />
          <h1 className="text-3xl font-bold text-white">Reserva de Inscripción</h1>
          <p className="text-white">¡Asegura tu lugar para una experiencia inolvidable!</p>
        </div>
      </div>

      <div className="flex-grow py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Participant Form */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-red-600">
              <h2 className="text-2xl font-semibold text-red-600 mb-4">Datos del Participante</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Nombre', field: 'firstName' },
                  { label: 'Apellido', field: 'lastName' },
                  { label: 'Correo Electrónico', field: 'email', type: 'email' },
                  { label: 'Contraseña', field: 'password', type: 'password' },
                  { label: 'Teléfono', field: 'phone' },
                  { label: 'Edad', field: 'age', type: 'number' }
                ].map(({ label, field, type }) => (
                  <div key={field}>
                    <label className="block text-black font-medium">{label} *</label>
                    <input
                      type={type||'text'}
                      value={participant[field]}
                      onChange={e => handleChange(field, e.target.value)}
                      className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                    />
                  </div>
                ))}

                {/* Role */}
                <div>
                  <label className="block text-black font-medium">Rol *</label>
                  <select
                    value={participant.role}
                    onChange={e=>handleChange('role',e.target.value)}
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  >
                    <option value="">Selecciona Rol</option>
                    <option value="chaperone">Acompañante</option>
                    <option value="musician">Músico</option>
                  </select>
                </div>

                {/* Instrument for Musicians */}
                {participant.role=== 'musician' && (
                  <div>
                    <label className="block text-black font-medium">Instrumento *</label>
                    <select
                      value={participant.instrument}
                      onChange={e=>handleChange('instrument',e.target.value)}
                      className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                    >
                      <option value="">Selecciona Instrumento</option>
                      {instrumentOptions.map(inst => <option key={inst} value={inst}>{inst}</option>)}
                    </select>
                  </div>
                )}

                {/* Nationality */}
                <div>
                  <label className="block text-black font-medium">Nacionalidad *</label>
                  <select
                    value={participant.nationality}
                    onChange={e=>handleChange('nationality',e.target.value)}
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  >
                    <option value="">Selecciona Nacionalidad</option>
                    {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-black font-medium">Dirección *</label>
                  <input
                    type="text"
                    value={participant.address}
                    onChange={e=>handleChange('address',e.target.value)}
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  />
                </div>

                {/* Dietary */}
                <div className="md:col-span-2">
                  <label className="block text-black font-medium">Restricciones Alimenticias (Opcional)</label>
                  <input
                    type="text"
                    value={participant.dietary}
                    onChange={e=>handleChange('dietary',e.target.value)}
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  />
                </div>

                {/* Single Bed */}
                <div className="flex items-center md:col-span-2">
                  <input
                    type="checkbox"
                    checked={participant.singleBed}
                    onChange={e=>handleChange('singleBed',e.target.checked)}
                    className="mr-2 accent-red-600"
                  />
                  <label className="text-black font-medium">Cama Individual (Extra $150)</label>
                </div>
              </div>
            </div>

            {/* Notice & Submit */}
            <div className="bg-white p-4 rounded shadow border border-red-600 text-sm text-black">
              <p className="font-semibold mb-2 text-red-600">Aviso sobre Alojamiento Compartido</p>
              <p>Para mantener la gira accesible, las habitaciones incluyen dos camas tamaño queen compartidas entre dos participantes, con una cama por persona.</p>
              <p className="mt-2">Ofrecemos la opción <span className="italic text-red-600">"Cama Individual"</span> (extra $150). Si seleccionas esta opción, se asignará una cama separada.</p>
              <p className="mt-2"><strong>Nota:</strong> Durante la reserva, si no seleccionas la opción de cama individual, podrías compartir habitación con otro participante.</p>
              <p className="mt-2">Esta opción está sujeta a disponibilidad y podría aplicar cargos adicionales. Gracias por tu comprensión.</p>
              <p className="mt-4 text-xs text-gray-600">¿Preguntas? Contáctanos en <a href="mailto:information@simanof.org" className="underline text-red-600">information@simanof.org</a> o llama al <a href="tel:+17737109953" className="underline text-red-600">(773) 710-9953</a>.</p>
            </div>

            <div className="text-center">
              <button type="submit" className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-500 font-semibold">Enviar Reserva</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
